// Draft priorities: the list the autopick draft picks from.
// PLAN-2026-09-24 item B; DRAFT_MODES §4; the endpoints are BACKLOG D2's.
//
// Where a manager sets draft priorities ahead of the autopick draft. So the
// screen's own word is PRIORITIES, not "plan" or "queue".
//
// TWO WAYS TO RANK, AND THE LEAGUE CHOOSES (DRAFT_MODES §4.1): one list with
// position limits, or separate lists per position with an order of
// positions. Which one is the commissioner's choice, made when the league is
// created (`draft.ranking`) and settled before anyone ranks, never a
// manager's, so this screen has no switch (B-264):
//
// - ONE LIST (ESPN's model): each of your picks is the highest player left on
//   your list that your limits allow (B-124).
// - BY POSITION (NFL.com's): a list per position, and a PICK ORDER naming a
//   position for every round — at least as many of each as the lineup starts,
//   which is how this style keeps a team whole without limits (B-132).
//
// Either way the house ranking picks when your list runs out (§4.5), and the
// screen says so, in those words, before anybody saves anything — a draft that
// runs at three in the morning with nobody watching is the moment "say what
// will happen" exists for. A plan saved in the other style is converted by the
// server when it is read (`convertedFrom`), and the screen says that too.
//
// SAVE IS A BUTTON, not every keystroke. Each save rebuilds the league's board
// over a season of stat lines on the server, and a list reordered twenty times
// is one decision, not twenty. What is unsaved is said on screen, at the save
// button, until it is saved.
//
// UP AND DOWN BUTTONS, NOT DRAG. They work at 390 with a thumb and with a
// keyboard; drag is a nicety a later pass can add over the same list. AND
// THE FAR MOVES IN ONE (B-415): **Move to top** on each row, and its number a
// field a place can be typed into, since forty places was forty taps; an
// empty list starts from the house ranking's top players, to edit from.
//
// LAYOUT. Phone: two tabs, My list and Players, with the status and the limits
// pinned above and the save bar below. Tablet and wider: the two side by side,
// the list first because it is the thing being made. Limits — and, by
// position, the pick order — are a summary line that opens a sheet on every
// width: a form wants its own surface, and a third column at 834 would squeeze
// the list's buttons off the row.

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  DraftPlanView,
  DraftPoolPlayer,
  DraftPoolView,
  PositionLimit,
  SaveDraftPlanRequest,
} from '@fsp/domain';
import { formatInZone, planProblem } from '@fsp/domain';
import { deviceZone, useReaderZone } from '../data/hooks';
import { Cap, EmptyState, PositionPill, ProTeamCode, positionLabel } from '../kit';
import { SplitScreen } from '../templates/SplitScreen';
import { DetailSheet } from '../templates/DetailSheet';
import { useAtLeast } from '../data/detail';
import { messageOf } from '../data/api';
import { saveDraftPlan, useDraftPlan, useDraftPool } from '../data/draftPlan';
import { FromLeague } from './shared/FromLeague';
import { useTier } from '../shell/useTier';
import { Icon } from '../kit/Icon';
import {
  Button,
  Chips,
  Choice,
  IconButton,
  Line,
  NumberField,
  Stack,
  Text,
  TextField,
  tell,
} from '../kit/ui';
import '../styles/screens/priorities.css';
import { clubLabel } from '../sports/registry';

/** How many pool rows to draw before asking for a search. A phone is not a spreadsheet. */
const POOL_PAGE = 100;

/** How many of the house ranking's players **Start from the rankings** lists (B-415). */
const START_FROM = 100;

export function PrioritiesScreen() {
  const { leagueId = null } = useParams();
  const plan = useDraftPlan(leagueId);
  const pool = useDraftPool(leagueId);
  return (
    <FromLeague snapshot={plan.snapshot}>
      {(view) =>
        view === null ? (
          <EmptyState>No draft priorities here.</EmptyState>
        ) : (
          <FromLeague snapshot={pool.snapshot}>
            {(ranking) =>
              ranking === null ? (
                <EmptyState>No players to rank yet.</EmptyState>
              ) : (
                <Editor leagueId={leagueId!} view={view} ranking={ranking} onSaved={plan.reload} />
              )
            }
          </FromLeague>
        )
      }
    </FromLeague>
  );
}

type Ordering = 'overall' | 'by-position';

/** The plan as the screen edits it: the parts of `SaveDraftPlanRequest` it changes. */
interface Working {
  ordering: Ordering;
  overall: string[];
  byPosition: Record<string, string[]>;
  /** By position: a position for every round, as the server hands it over. One list: empty. */
  roundPlan: string[];
  limits: Record<string, PositionLimit>;
  /** An auction league's: the most I would pay for each player on my list (AUCTION.md §7). */
  prices: Record<string, number>;
}

function workingOf(view: DraftPlanView): Working {
  const p = view.plan;
  return {
    ordering: p.ordering,
    overall: [...p.overall],
    byPosition: Object.fromEntries(Object.entries(p.byPosition).map(([k, v]) => [k, [...v]])),
    roundPlan: [...p.roundPlan],
    limits: { ...p.limits },
    prices: { ...(p.prices ?? {}) },
  };
}

function sameWorking(a: Working, b: Working): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Every player a working plan names, whichever way it is ranked. */
function namedIn(w: Working): string[] {
  return w.ordering === 'overall' ? w.overall : Object.values(w.byPosition).flat();
}

/** "1 QB · 2 RB · 2 WR": what the lineup starts, which a pick order must name. */
function startsSummary(
  minimumPicks: Readonly<Record<string, number>>,
  positions: readonly string[],
) {
  return positions
    .filter((p) => (minimumPicks[p] ?? 0) > 0)
    .map((p) => `${minimumPicks[p]} ${positionLabel(p)}`)
    .join(' · ');
}

function Editor({
  leagueId,
  view,
  ranking,
  onSaved,
}: {
  leagueId: string;
  view: DraftPlanView;
  ranking: DraftPoolView;
  onSaved: () => void;
}) {
  const saved = useMemo(() => workingOf(view), [view]);
  const [work, setWork] = useState<Working>(saved);
  const [busy, setBusy] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  // Which position's list is on screen, by position; the pool follows it.
  const [listPosition, setListPosition] = useState<string>(view.positions[0] ?? '');
  const [poolPosition, setPoolPosition] = useState<string>('all');
  // A fresh read is what was just saved (the plan is read once, and again
  // after each save), so it replaces what is on screen.
  useEffect(() => setWork(saved), [saved]);

  const byId = useMemo(() => new Map(ranking.players.map((p) => [p.playerId, p])), [ranking]);
  const locked = view.locked;
  const ready = view.plan.submittedAt !== null;
  // Players on the list the pool no longer has — a player cut, retired, or
  // moved to a position this league cannot hold. The server refuses a plan
  // that names one, so they leave on the next save, and the row says so.
  const gone = namedIn(work).filter((id) => !byId.has(id));
  // AND THAT SAVE IS OWED, so it counts as unsaved: the notice says saving
  // takes them off, and a disabled Save beside it was a dead end (review of
  // 2026-09-24).
  // A plan the server converted to the league's style is the same kind of
  // owed save: it is shown converted and stored as it was until saved.
  const dirty = !sameWorking(work, saved) || gone.length > 0 || view.convertedFrom !== null;
  const byPos = work.ordering === 'by-position';
  // AN AUCTION LEAGUE PRICES ITS LIST (AUCTION.md §7): each player's price
  // starts at his value, and is what my seat bids up to.
  const auction = ranking.auction;
  /** The most a price can be: the budget, less the lowest bid for every other spot. */
  const priceCeiling =
    auction === null ? 0 : auction.budget - auction.minBid * Math.max(0, auction.spots - 1);

  const bodyOf = (submit: boolean): SaveDraftPlanRequest => {
    // TODO(C9): the do-not-draft list has no control here yet; a stored one is saved back as it was.
    const inPool = (ids: readonly string[]) => ids.filter((id) => byId.has(id));
    return byPos
      ? {
          ordering: 'by-position',
          overall: [],
          byPosition: Object.fromEntries(
            Object.entries(work.byPosition)
              .map(([pos, ids]) => [pos, inPool(ids)] as const)
              .filter(([, ids]) => ids.length > 0),
          ),
          limits: {},
          roundPlan: work.roundPlan,
          doNotDraft: inPool(view.plan.doNotDraft),
          submit,
        }
      : {
          ordering: 'overall',
          overall: inPool(work.overall),
          byPosition: {},
          limits: work.limits,
          roundPlan: [],
          doNotDraft: inPool(view.plan.doNotDraft),
          // THE PRICES OF THE PLAYERS ON THE LIST, and nobody else's.
          ...(auction === null
            ? {}
            : {
                prices: Object.fromEntries(
                  inPool(work.overall).flatMap((id) =>
                    work.prices[id] === undefined ? [] : [[id, work.prices[id]!] as const],
                  ),
                ),
              }),
          submit,
        };
  };
  // THE SERVER'S OWN CHECK, run before Save rather than after it: a pick order
  // that leaves out the kicker is said at the button, in the sentence the
  // server would refuse it with.
  const problem = planProblem(bodyOf(false), {
    positions: view.positions,
    rounds: view.rounds,
    ranking: view.plan.ordering,
    minimumPicks: view.minimumPicks,
    leagueCaps: view.leagueCaps,
  });

  const save = async (submit: boolean) => {
    const body = bodyOf(submit);
    setBusy(true);
    const reply = await saveDraftPlan(leagueId, body);
    setBusy(false);
    if (reply.status === 'ok') {
      tell(submit ? 'Saved and marked ready.' : 'Saved.', 'good');
      onSaved();
      return;
    }
    tell(
      messageOf(reply, 'That did not save.', 'Could not reach the site. Nothing was saved.'),
      reply.status === 'refused' ? 'warn' : 'bad',
    );
  };

  // One list, or the list for the player's own position — whichever this
  // plan is ranked by. Add goes to the end; the list is best first.
  const listFor = (w: Working, id: string): string | null =>
    w.ordering === 'overall' ? null : (byId.get(id)?.positions[0] ?? null);
  const edit = (w: Working, pos: string | null, fn: (ids: string[]) => string[]): Working =>
    pos === null
      ? { ...w, overall: fn(w.overall) }
      : { ...w, byPosition: { ...w.byPosition, [pos]: fn(w.byPosition[pos] ?? []) } };
  const add = (id: string) =>
    setWork((w) => {
      const added = edit(w, listFor(w, id), (ids) => (ids.includes(id) ? ids : [...ids, id]));
      // A PLAYER ADDED IN AN AUCTION LEAGUE IS PRICED AT HIS VALUE, to change.
      if (auction === null || added.prices[id] !== undefined) return added;
      const start = Math.min(priceCeiling, byId.get(id)?.value ?? auction.minBid);
      return { ...added, prices: { ...added.prices, [id]: start } };
    });
  const price = (id: string, amount: number) =>
    setWork((w) => ({ ...w, prices: { ...w.prices, [id]: amount } }));
  const remove = (id: string) =>
    setWork((w) =>
      // A player gone from the pool has no position to find a list by, so a
      // by-position remove looks in every list.
      w.ordering === 'overall'
        ? {
            ...w,
            overall: w.overall.filter((x) => x !== id),
            prices: Object.fromEntries(Object.entries(w.prices).filter(([x]) => x !== id)),
          }
        : {
            ...w,
            byPosition: Object.fromEntries(
              Object.entries(w.byPosition).map(([k, v]) => [k, v.filter((x) => x !== id)]),
            ),
          },
    );
  const move = (index: number, by: -1 | 1) =>
    setWork((w) =>
      edit(w, w.ordering === 'overall' ? null : listPosition, (ids) => {
        const to = index + by;
        if (to < 0 || to >= ids.length) return ids;
        const next = [...ids];
        [next[index], next[to]] = [next[to]!, next[index]!];
        return next;
      }),
    );
  /** A row to a place in one move (B-415): the top, or a typed number, held to the list. */
  const place = (index: number, to: number) =>
    setWork((w) =>
      edit(w, w.ordering === 'overall' ? null : listPosition, (ids) => {
        const at = Math.max(0, Math.min(ids.length - 1, to));
        if (index < 0 || index >= ids.length || at === index) return ids;
        const next = [...ids];
        const [id] = next.splice(index, 1);
        next.splice(at, 0, id!);
        return next;
      }),
    );
  /**
   * AN EMPTY LIST STARTS FROM THE HOUSE RANKING (B-415): its top players, in
   * its order, each on its position's list when the league ranks by position,
   * for the manager to edit rather than build from nothing.
   */
  const startFromRankings = () =>
    setWork((w) => {
      const top = ranking.players.slice(0, START_FROM);
      if (w.ordering === 'overall') return { ...w, overall: top.map((p) => p.playerId) };
      const byPosition: Record<string, string[]> = { ...w.byPosition };
      for (const p of top) {
        const pos = p.positions[0];
        if (pos === undefined) continue;
        byPosition[pos] = [...(byPosition[pos] ?? []), p.playerId];
      }
      return { ...w, byPosition };
    });

  const pickPosition = (pos: string) => {
    setListPosition(pos);
    setPoolPosition(pos);
  };
  const filterPool = (pos: string) => {
    setPoolPosition(pos);
    if (pos !== 'all') setListPosition(pos);
  };

  // Where each listed player sits, for the pool's rows.
  const listedNote = useMemo(() => {
    const notes = new Map<string, string>();
    if (work.ordering === 'overall') {
      work.overall.forEach((id, i) => notes.set(id, `#${i + 1} on your list`));
    } else {
      for (const [pos, ids] of Object.entries(work.byPosition)) {
        ids.forEach((id, i) => notes.set(id, `#${i + 1} on your ${positionLabel(pos)} list`));
      }
    }
    return notes;
  }, [work]);

  const status = (
    <Status
      view={view}
      ordering={work.ordering}
      limits={work.limits}
      roundPlan={work.roundPlan}
      positions={view.positions}
      onLimits={() => setLimitsOpen(true)}
      onOrder={() => setOrderOpen(true)}
      gone={gone.length}
      auction={
        auction === null
          ? null
          : {
              budget: auction.budget,
              total: work.overall.reduce((sum, id) => sum + (work.prices[id] ?? 0), 0),
            }
      }
    />
  );
  const listed = byPos ? (work.byPosition[listPosition] ?? []) : work.overall;
  const count = namedIn(work).length;

  return (
    <>
      <SplitScreen
        status={status}
        ratio={[5, 5]}
        regions={[
          {
            id: 'list',
            title: byPos ? 'My lists' : 'My list',
            badge: count > 0 ? <Cap> {count}</Cap> : undefined,
            content: (
              <div>
                {byPos ? (
                  <PositionChips
                    positions={view.positions}
                    value={listPosition}
                    counts={work.byPosition}
                    onChange={pickPosition}
                  />
                ) : null}
                <MyList
                  ids={listed}
                  byId={byId}
                  locked={locked}
                  position={byPos ? listPosition : null}
                  onMove={move}
                  onPlace={place}
                  onStart={count === 0 && !locked ? startFromRankings : null}
                  onRemove={remove}
                  prices={
                    auction === null || byPos
                      ? null
                      : { of: work.prices, ceiling: priceCeiling, min: auction.minBid, set: price }
                  }
                />
              </div>
            ),
          },
          {
            id: 'players',
            title: 'Players',
            content: (
              <Pool
                ranking={ranking}
                positions={view.positions}
                listed={listedNote}
                position={poolPosition}
                onPosition={filterPool}
                locked={locked}
                onAdd={add}
                onRemove={remove}
              />
            ),
          },
        ]}
        footer={
          <SaveBar
            locked={locked}
            dirty={dirty}
            ready={ready}
            busy={busy}
            problem={problem}
            onSave={() => void save(false)}
            onReady={() => void save(true)}
          />
        }
      />
      <DetailSheet opened={limitsOpen} onClose={() => setLimitsOpen(false)} title="Position limits">
        <LimitsForm
          positions={view.positions}
          limits={work.limits}
          locked={locked}
          onChange={(limits) => setWork((w) => ({ ...w, limits }))}
        />
      </DetailSheet>
      <DetailSheet opened={orderOpen} onClose={() => setOrderOpen(false)} title="Pick order">
        <RoundPlanForm
          positions={view.positions}
          plan={work.roundPlan}
          starts={startsSummary(view.minimumPicks, view.positions)}
          problem={problem}
          locked={locked}
          onChange={(roundPlan) => setWork((w) => ({ ...w, roundPlan }))}
        />
      </DetailSheet>
    </>
  );
}

/** "Thursday 2:00 AM MDT", in the reader's zone and named (B-181). */
function whenOf(at: string, zone: string): string {
  return formatInZone(at, zone, {
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "in 4 hours", "in 12 minutes", "in 3 days". */
function untilOf(at: string, now: number): string {
  const ms = Date.parse(at) - now;
  if (ms <= 0) return 'now';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in ${days} days`;
}

/** The wall clock, once a minute. The countdown is the only thing that reads it. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** "1 RB · 2 WR · 3–5 TE · 6 QB": each run of one position, once. */
function pickOrderSummary(plan: readonly string[]): string {
  const parts: string[] = [];
  let start = 0;
  plan.forEach((choice, i) => {
    if (plan[i + 1] === choice) return;
    parts.push(`${start === i ? i + 1 : `${start + 1}–${i + 1}`} ${positionLabel(choice)}`);
    start = i + 1;
  });
  return parts.join(' · ');
}

/** What the server did to a plan saved in the other style, said once, plainly. */
function convertedNote(from: Ordering): string {
  return from === 'overall'
    ? 'This league now ranks by position. Your list has been split into a list for each position, each in the order you had it, and given a pick order. Save to keep it this way.'
    : 'This league now ranks in one list. Your lists have been put into one: within a position your order is kept, and between positions the house ranking decides. Save to keep it this way.';
}

function Status({
  view,
  ordering,
  limits,
  roundPlan,
  positions,
  onLimits,
  onOrder,
  gone,
  auction,
}: {
  view: DraftPlanView;
  ordering: Ordering;
  limits: Record<string, PositionLimit>;
  roundPlan: readonly string[];
  positions: readonly string[];
  onLimits: () => void;
  onOrder: () => void;
  gone: number;
  /** An auction league's budget, and what my prices add up to. */
  auction: { budget: number; total: number } | null;
}) {
  const now = useMinute();
  const zone = useReaderZone() ?? deviceZone();
  const at = view.lockedAt;
  const summary = positions
    .filter((p) => limits[p] !== undefined)
    .map((p) => {
      const l = limits[p]!;
      const name = positionLabel(p);
      return l.min === l.max ? `${name} ${l.min}` : `${name} ${l.min}–${l.max}`;
    })
    .join(' · ');
  return (
    <Stack gap={2} className="fsp-priorities-status">
      <Line spread>
        <Text weight="bold">
          {at === null
            ? 'No draft time is set yet.'
            : view.locked
              ? `The draft started ${whenOf(at, zone)}. Your list is closed.`
              : `The draft runs by itself ${whenOf(at, zone)}, ${untilOf(at, now)}.`}
        </Text>
        {view.plan.submittedAt !== null ? (
          <span className="fsp-ready-chip">
            <Icon name="accept" size="sm" /> Ready
          </span>
        ) : null}
      </Line>
      {view.locked ? null : auction !== null ? (
        <Text size="xs" tone="secondary" block>
          This league’s draft is an auction. When you are away, or it runs by itself, your seat bids
          up to your price for each player on your list, and up to the value for anybody else; your
          limits still hold. The order of your list does not matter, only the prices.
        </Text>
      ) : (
        <Text size="xs" tone="secondary" block>
          {ordering === 'overall'
            ? 'This league ranks in one list. Each of your picks is the highest player left on your list that your limits allow. When your list runs out, the house ranking picks for you.'
            : 'This league ranks by position. Each round takes the position your pick order names: the highest player left on your list for it, or the house ranking’s best there once your list runs out.'}{' '}
          The draft runs at that time whether or not you mark ready.
        </Text>
      )}
      {auction !== null ? (
        <Text size="xs" tone={auction.total > auction.budget ? 'warn' : 'secondary'} block>
          Your prices add up to ${auction.total}; your budget is ${auction.budget}.
          {auction.total > auction.budget
            ? ' Your seat can never spend more than the budget, so not every one of them can be paid.'
            : ''}
        </Text>
      ) : null}
      {view.convertedFrom !== null && !view.locked ? (
        <Text size="xs" tone="warn" block>
          {convertedNote(view.convertedFrom)}
        </Text>
      ) : null}
      {gone > 0 ? (
        <Text size="xs" tone="warn" block>
          {gone === 1
            ? 'One player on your list is no longer in the draft pool. Saving takes that player off.'
            : `${gone} players on your list are no longer in the draft pool. Saving takes them off.`}
        </Text>
      ) : null}
      {ordering === 'overall' ? (
        <Line>
          <div className="fsp-grow">
            <Text size="xs" tone="secondary" nowrap block>
              Limits: {summary || 'none'}
            </Text>
          </div>
          <Button size="xs" onClick={onLimits} label={view.locked ? 'Limits' : 'Change limits'}>
            {view.locked ? 'Limits' : 'Change'}
          </Button>
        </Line>
      ) : (
        <Line>
          <div className="fsp-grow">
            <Text size="xs" tone="secondary" nowrap block>
              Pick order: {pickOrderSummary(roundPlan)}
            </Text>
          </div>
          <Button
            size="xs"
            onClick={onOrder}
            label={view.locked ? 'Pick order' : 'Change pick order'}
          >
            {view.locked ? 'Order' : 'Change'}
          </Button>
        </Line>
      )}
    </Stack>
  );
}

/** The by-position lists' tabs, each with how many it holds. */
function PositionChips({
  positions,
  value,
  counts,
  onChange,
}: {
  positions: readonly string[];
  value: string;
  counts: Readonly<Record<string, readonly string[]>>;
  onChange: (pos: string) => void;
}) {
  return (
    <div className="fsp-priorities-chips">
      <Chips
        label="Which position's list"
        single
        options={positions.map((p) => {
          const n = counts[p]?.length ?? 0;
          return { value: p, label: `${positionLabel(p)}${n > 0 ? ` ${n}` : ''}` };
        })}
        value={[value]}
        onChange={(v) => v[0] && onChange(v[0])}
      />
    </div>
  );
}

function MyList({
  ids,
  byId,
  locked,
  position,
  onMove,
  onPlace,
  onStart,
  onRemove,
  prices,
}: {
  ids: readonly string[];
  byId: ReadonlyMap<string, DraftPoolPlayer>;
  locked: boolean;
  /** The position this list is for, by position; null for one list. */
  position: string | null;
  onMove: (index: number, by: -1 | 1) => void;
  /** To a place in one move: 0 the top (B-415). */
  onPlace: (index: number, to: number) => void;
  /** Fills every empty list from the house ranking; null once anything is listed. */
  onStart: (() => void) | null;
  onRemove: (id: string) => void;
  /** An auction league's prices, each player's most; null in a pick draft. */
  prices: {
    of: Readonly<Record<string, number>>;
    ceiling: number;
    min: number;
    set: (id: string, amount: number) => void;
  } | null;
}) {
  const numbers = useAtLeast('standard');
  if (ids.length === 0) {
    return (
      <EmptyState>
        <Stack gap={2}>
          <span>
            {prices !== null
              ? 'Your list is empty. Add players from the Players list and set the most you would pay for each. With no list, your seat bids up to the values, within your limits.'
              : position === null
                ? 'Your list is empty. Add players from the Players list, best first. With no list, the draft picks for you from the house ranking, within your limits.'
                : `Your ${positionLabel(position)} list is empty. Add players from the Players list, best first. A round that asks for ${positionLabel(position)} with nobody left here takes the house ranking’s best one.`}
          </span>
          {onStart !== null && prices === null ? (
            <span>
              <Button size="xs" variant="secondary" onClick={onStart}>
                Start from the rankings
              </Button>
            </span>
          ) : null}
          {onStart !== null && prices === null ? (
            <span className="fsp-sub">
              {position === null
                ? `Lists the house ranking’s top ${START_FROM}, in its order, to change as you like.`
                : `Lists the house ranking’s top ${START_FROM} on their positions’ lists, in its order, to change as you like.`}
            </span>
          ) : null}
        </Stack>
      </EmptyState>
    );
  }
  return (
    <div
      role="list"
      aria-label={position === null ? 'My list' : `My ${positionLabel(position)} list`}
    >
      {ids.map((id, i) => {
        const p = byId.get(id);
        return (
          <div
            key={id}
            role="listitem"
            className={`fsp-row fsp-priorities-row${p ? '' : ' fsp-row-problem'}`}
          >
            {locked || prices !== null ? (
              <span className="fsp-num fsp-muted" aria-label={`Number ${i + 1}`}>
                {i + 1}
              </span>
            ) : (
              <RankField
                rank={i + 1}
                of={ids.length}
                name={p?.name ?? 'this player'}
                onRank={(to) => onPlace(i, to - 1)}
              />
            )}
            {p ? <PositionPill pos={p.positions[0] ?? '?'} /> : <span />}
            <div className="fsp-grow">
              <div className="fsp-name">
                <span className="fsp-name-text">{p ? p.name : 'No longer in the pool'}</span>
              </div>
              <div className="fsp-sub">
                {p ? (
                  <>
                    <ProTeamCode code={p.proTeam} />
                    {numbers && p.perGame !== null ? ` · ${p.perGame.toFixed(1)} a game` : ''}
                    {numbers ? ` · house #${p.rank}` : ''}
                    {numbers && prices !== null && p.value !== null ? ` · value $${p.value}` : ''}
                  </>
                ) : (
                  'Removed when you save.'
                )}
              </div>
            </div>
            {prices !== null && p ? (
              <div className="fsp-priorities-price">
                <NumberField
                  label={`The most you would pay for ${p.name}`}
                  hideLabel
                  prefix="$"
                  value={prices.of[id] ?? p.value ?? prices.min}
                  min={prices.min}
                  max={prices.ceiling}
                  disabled={locked}
                  onChange={(v) => v !== null && prices.set(id, v)}
                />
              </div>
            ) : null}
            {locked ? (
              <span />
            ) : prices !== null ? (
              // AN AUCTION'S LIST HAS NO ORDER that matters, only prices: no moves.
              <IconButton
                icon="close"
                variant="quiet"
                label={`Remove ${p?.name ?? 'this player'}`}
                onClick={() => onRemove(id)}
              />
            ) : (
              <Line gap={1}>
                <IconButton
                  icon="top"
                  variant="quiet"
                  label={`Move ${p?.name ?? 'this player'} to the top`}
                  disabled={i === 0}
                  onClick={() => onPlace(i, 0)}
                />
                <IconButton
                  icon="up"
                  variant="quiet"
                  label={`Move ${p?.name ?? 'this player'} up`}
                  disabled={i === 0}
                  onClick={() => onMove(i, -1)}
                />
                <IconButton
                  icon="down"
                  variant="quiet"
                  label={`Move ${p?.name ?? 'this player'} down`}
                  disabled={i === ids.length - 1}
                  onClick={() => onMove(i, 1)}
                />
                <IconButton
                  icon="close"
                  variant="quiet"
                  label={`Remove ${p?.name ?? 'this player'}`}
                  onClick={() => onRemove(id)}
                />
              </Line>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A row's place as a field (B-415): its number, which a place typed over and
 * left — Enter, Tab or a tap elsewhere — moves him to, held to the list's
 * length. Anything that is not a place puts the number back.
 */
function RankField({
  rank,
  of,
  name,
  onRank,
}: {
  rank: number;
  of: number;
  name: string;
  onRank: (to: number) => void;
}) {
  const [text, setText] = useState(String(rank));
  useEffect(() => setText(String(rank)), [rank]);
  const commit = () => {
    const to = Number.parseInt(text, 10);
    if (Number.isFinite(to) && to >= 1 && to !== rank) onRank(Math.min(to, of));
    else setText(String(rank));
  };
  return (
    <input
      className="fsp-priorities-rank fsp-num"
      type="text"
      inputMode="numeric"
      aria-label={`Place of ${name}, 1 to ${of}`}
      value={text}
      onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, ''))}
      onFocus={(e) => e.target.select()}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') setText(String(rank));
      }}
    />
  );
}

function Pool({
  ranking,
  positions,
  listed,
  position,
  onPosition,
  locked,
  onAdd,
  onRemove,
}: {
  ranking: DraftPoolView;
  positions: readonly string[];
  /** Everybody on a list, and where: "#3 on your list". */
  listed: ReadonlyMap<string, string>;
  position: string;
  onPosition: (pos: string) => void;
  locked: boolean;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const numbers = useAtLeast('standard');
  const everything = useAtLeast('pro');
  const tier = useTier();
  const [query, setQuery] = useState('');
  // Only positions somebody in the pool plays, in the sport's own order.
  const present = useMemo(() => {
    const held = new Set(ranking.players.map((p) => p.positions[0]));
    return positions.filter((p) => held.has(p));
  }, [ranking, positions]);
  const q = query.trim().toLowerCase();
  const matching = ranking.players.filter(
    (p) =>
      (position === 'all' || p.positions[0] === position) &&
      (q === '' ||
        p.name.toLowerCase().includes(q) ||
        (p.proTeam !== null &&
          (p.proTeam.toLowerCase() === q || clubLabel(p.proTeam).toLowerCase() === q))),
  );
  const shown = matching.slice(0, POOL_PAGE);

  return (
    <div>
      <Stack
        gap={2}
        className={`fsp-priorities-pool-head${tier === 'phone' ? ' fsp-priorities-pool-head-phone' : ''}`}
      >
        <TextField
          label="Search players"
          hideLabel
          placeholder="Search by name or team"
          icon="search"
          value={query}
          onChange={setQuery}
        />
        <Chips
          label="Position"
          single
          options={[
            { value: 'all', label: 'All' },
            ...present.map((p) => ({ value: p, label: positionLabel(p) })),
          ]}
          value={[position]}
          onChange={(v) => v[0] && onPosition(v[0])}
        />
      </Stack>
      {/* What the order is scrolls away with the list; the search and the
          positions stay (B-486). */}
      <div className="fsp-priorities-pool-key">
        <Text size="xs" tone="secondary" block>
          In the order the house ranking would draft them: last season’s points per game in this
          league’s scoring, weighed by how deep each position is. Not a projection.
        </Text>
      </div>
      {shown.length === 0 ? (
        <EmptyState>Nobody matches that.</EmptyState>
      ) : (
        <div role="list" aria-label="Players">
          {shown.map((p) => {
            const at = listed.get(p.playerId);
            return (
              <div
                key={p.playerId}
                role="listitem"
                className={`fsp-row ${numbers ? 'fsp-priorities-pool-row' : 'fsp-priorities-pool-row-plain'}${at ? ' fsp-row-selected' : ''}`}
              >
                {numbers ? <span className="fsp-num fsp-muted">{p.rank}</span> : null}
                <PositionPill pos={p.positions[0] ?? '?'} />
                <div className="fsp-grow">
                  <div className="fsp-name">
                    <span className="fsp-name-text">{p.name}</span>
                  </div>
                  <div className="fsp-sub">
                    <ProTeamCode code={p.proTeam} />
                    {numbers && p.perGame !== null ? ` · ${p.perGame.toFixed(1)} a game` : ''}
                    {numbers && p.value !== null ? ` · value $${p.value}` : ''}
                    {everything && p.overReplacement !== null
                      ? ` · ${p.overReplacement >= 0 ? '+' : ''}${p.overReplacement.toFixed(1)} over a starter`
                      : ''}
                    {at ? ` · ${at}` : ''}
                  </div>
                </div>
                {locked ? (
                  <span />
                ) : at ? (
                  <Button
                    size="xs"
                    label={`Take ${p.name} off your list`}
                    onClick={() => onRemove(p.playerId)}
                  >
                    Remove
                  </Button>
                ) : (
                  <Button
                    size="xs"
                    variant="soft"
                    icon="add"
                    label={`Add ${p.name} to your list`}
                    onClick={() => onAdd(p.playerId)}
                  >
                    Add
                  </Button>
                )}
              </div>
            );
          })}
          {matching.length > shown.length ? (
            <div className="fsp-priorities-more">
              <Text size="xs" tone="secondary" block center>
                Showing the first {shown.length} of {matching.length}. Search to find the rest.
              </Text>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SaveBar({
  locked,
  dirty,
  ready,
  busy,
  problem,
  onSave,
  onReady,
}: {
  locked: boolean;
  dirty: boolean;
  ready: boolean;
  busy: boolean;
  /** Why the server would refuse this plan, or null: said here, and nothing saves. */
  problem: string | null;
  onSave: () => void;
  onReady: () => void;
}) {
  if (locked) {
    return <Text tone="secondary">The draft has started. Nothing here can change now.</Text>;
  }
  return (
    // WRAPPED ON A PHONE, so "Save and mark ready" is never cut off at the edge.
    <Line spread wrap className="fsp-priorities-save">
      <Text size="xs" tone={dirty || problem !== null ? 'warn' : 'secondary'}>
        {problem !== null
          ? problem
          : dirty
            ? 'Unsaved changes.'
            : ready
              ? 'Saved and ready. You can still change it until the draft.'
              : 'Saved.'}
      </Text>
      <Line>
        <Button
          variant={dirty ? 'primary' : 'secondary'}
          disabled={!dirty || busy || problem !== null}
          onClick={onSave}
        >
          Save
        </Button>
        {ready && !dirty ? null : (
          <Button
            variant={dirty ? 'secondary' : 'primary'}
            disabled={busy || problem !== null}
            icon="accept"
            onClick={onReady}
          >
            {dirty ? 'Save and mark ready' : 'Mark ready'}
          </Button>
        )}
      </Line>
    </Line>
  );
}

function LimitsForm({
  positions,
  limits,
  locked,
  onChange,
}: {
  positions: readonly string[];
  limits: Record<string, PositionLimit>;
  locked: boolean;
  onChange: (limits: Record<string, PositionLimit>) => void;
}) {
  const set = (pos: string, next: PositionLimit) => onChange({ ...limits, [pos]: next });
  return (
    <Stack gap={4}>
      <Text tone="secondary" block>
        The draft never gives you more than the most at a position. It saves your last picks for the
        fewest, so they are never spent early. Save on the main page to keep these.
      </Text>
      {positions.map((pos) => {
        const l = limits[pos] ?? { min: 0, max: 99 };
        return (
          <Line key={pos} spread>
            <PositionPill pos={pos} />
            <Stepper
              label={`Fewest ${pos}`}
              caption="Fewest"
              value={l.min}
              disabled={locked}
              canDown={l.min > 0}
              canUp={l.min < l.max}
              onChange={(min) => set(pos, { ...l, min })}
            />
            <Stepper
              label={`Most ${pos}`}
              caption="Most"
              value={l.max}
              disabled={locked}
              canDown={l.max > l.min}
              canUp={l.max < 99}
              onChange={(max) => set(pos, { ...l, max })}
            />
          </Line>
        );
      })}
    </Stack>
  );
}

function RoundPlanForm({
  positions,
  plan,
  starts,
  problem,
  locked,
  onChange,
}: {
  positions: readonly string[];
  plan: readonly string[];
  /** "1 QB · 2 RB · …": what the lineup starts, which the order must name. */
  starts: string;
  problem: string | null;
  locked: boolean;
  onChange: (plan: string[]) => void;
}) {
  // A POSITION IN EVERY ROUND, and nothing else to choose (2026-10-08). "Best
  // available" across lists ranked separately had no order of its own to
  // follow, and a pick order that names every round is also how many of each
  // the draft takes — which is what keeps a team whole without limits.
  const choices = positions.map((p) => ({ value: p, label: positionLabel(p) }));
  return (
    <Stack gap={2}>
      <Text tone="secondary" block>
        Which of your lists each round takes from: the highest player left on that list, or the
        house ranking’s best at that position once your list runs out.
        {starts ? ` The lineup starts ${starts}, so name each at least that many times.` : ''} It
        starts as the house’s: the order a typical team in this league takes each position when the
        house ranking picks for it. Save on the main page to keep these.
      </Text>
      {problem !== null && !locked ? (
        <Text size="xs" tone="warn" block>
          {problem}
        </Text>
      ) : null}
      {plan.map((choice, i) => (
        <Line key={i} spread>
          <span className="fsp-priorities-round">
            <Text weight="strong">Round {i + 1}</Text>
          </span>
          <div className="fsp-priorities-round-choice">
            <Choice
              label={`Round ${i + 1}`}
              hideLabel
              searchable={false}
              required
              options={choices}
              value={choice}
              disabled={locked}
              onChange={(v) => {
                if (v === null) return;
                const next = [...plan];
                next[i] = v;
                onChange(next);
              }}
            />
          </div>
        </Line>
      ))}
    </Stack>
  );
}

function Stepper({
  label,
  caption,
  value,
  disabled,
  canDown,
  canUp,
  onChange,
}: {
  label: string;
  caption: string;
  value: number;
  disabled: boolean;
  canDown: boolean;
  canUp: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className="fsp-priorities-stepper" aria-label={label} role="group">
      <span className="fsp-priorities-stepper-caption">
        <Text size="xs" tone="secondary">
          {caption}
        </Text>
      </span>
      <IconButton
        icon="remove"
        variant="secondary"
        label={`${label}: one fewer`}
        disabled={disabled || !canDown}
        onClick={() => onChange(value - 1)}
      />
      <span className="fsp-num fsp-priorities-stepper-value" aria-live="polite">
        {value}
      </span>
      <IconButton
        icon="add"
        variant="secondary"
        label={`${label}: one more`}
        disabled={disabled || !canUp}
        onClick={() => onChange(value + 1)}
      />
    </div>
  );
}
